import React from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { Alert } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useGetAccount } from '~/hooks/apis/offers/useGetAccount';

import AccountListPageHeader from '../AccountListPageHeader';
import { useAccountRelatedFullList } from '../useAccountRelatedRows';

import ContactMethodLink from './ContactMethodLink';
import { toAccountContact } from './toAccountContact';
import type { AccountContact } from './types';

const sharedOpts: Partial<GridColDef<AccountContact>> = {
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
};

const columns: Array<GridColDef<AccountContact>> = [
  { field: 'name', headerName: 'Contact Name', flex: 1, minWidth: 180, ...sharedOpts },
  {
    field: 'title',
    headerName: 'Title',
    flex: 1,
    minWidth: 180,
    renderCell: ({ row }) => row.title || '—',
    ...sharedOpts,
  },
  {
    field: 'email',
    headerName: 'Email',
    flex: 1,
    minWidth: 220,
    renderCell: ({ row }) => <ContactMethodLink kind="email" value={row.email} />,
    ...sharedOpts,
  },
  {
    field: 'mobilePhone',
    headerName: 'Mobile',
    flex: 1,
    minWidth: 150,
    renderCell: ({ row }) => row.mobilePhone || '—',
    ...sharedOpts,
  },
  {
    field: 'phone',
    headerName: 'Phone',
    flex: 1,
    minWidth: 150,
    renderCell: ({ row }) => <ContactMethodLink kind="phone" value={row.phone} />,
    ...sharedOpts,
  },
];

export default function AccountContactsListPage() {
  const { id_account: accountId } = useParams<{ id_account: string }>();
  const { data: account } = useGetAccount(accountId);
  const {
    count,
    error,
    isLoading,
    isTruncated,
    rows: contacts,
  } = useAccountRelatedFullList(accountId, 'contacts', toAccountContact);

  return (
    <>
      <Helmet>
        <title>Contacts | {account?.name || accountId}</title>
      </Helmet>

      <AccountListPageHeader
        accountId={accountId}
        count={count}
        isTruncated={isTruncated}
        sortedBy="Contact Name"
        title="Contacts"
      />

      {error ? (
        <Alert severity="error">Could not load contacts: {error.message}</Alert>
      ) : (
        <DataGrid
          autoHeight
          disableRowSelectionOnClick
          columns={columns}
          loading={isLoading}
          rows={contacts}
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
            sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
          }}
          pageSizeOptions={[25, 50, 100]}
        />
      )}
    </>
  );
}
